/**
 * KAN-912: удаление нашего сообщения переписки. Адрес запроса (для правила
 * api_access менеджера), можно ли предложить удаление на пузыре и текст
 * итога для toast по ответу сервера.
 */
import { canUserCallApi, type ApiAccessRule } from '@/lib/managerAccess';
import type { EstimateMessage } from '@/types/estimate';
import type { DeleteClientMessageResponse, DeleteClientMessageScope } from '@/types/client';

export interface DeleteResultNotice {
  kind: 'success' | 'error';
  text: string;
}

const FALLBACK_ERROR = 'Не удалось удалить сообщение';

/** Адрес DELETE сообщения клиента (тот же, что зовёт clientsApi). */
export function clientMessageDeleteUrl(clientId: string, messageId: string): string {
  return `/api/v1/clients/${clientId}/messages/${messageId}/`;
}

/** Наше сообщение, которое сервер разрешил удалить; входящие клиента никогда. */
export function isMessageDeletable(message: EstimateMessage): boolean {
  return message.role === 'assistant' && message.can_delete === true;
}

/**
 * Пункт "Удалить" доступен пользователю: сообщение удаляемое и (для
 * менеджера) правило api_access открывает DELETE этого сообщения.
 */
export function canDeleteMessage(
  user: { role?: string; api_access?: readonly ApiAccessRule[] | null } | null | undefined,
  clientId: string | null | undefined,
  message: EstimateMessage
): boolean {
  if (!clientId || !isMessageDeletable(message)) return false;
  return canUserCallApi(user, 'DELETE', clientMessageDeleteUrl(clientId, message.id));
}

function capitalize(text: string): string {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

/** Текст итога удаления по ответу сервера. */
export function deleteResultNotice(
  result: DeleteClientMessageResponse,
  scope: DeleteClientMessageScope
): DeleteResultNotice {
  const reason = (result.reason ?? '').trim();
  if (!result.deleted_in_history) {
    return { kind: 'error', text: reason ? capitalize(reason) : FALLBACK_ERROR };
  }
  if (result.cancelled_before_send) {
    return { kind: 'success', text: 'Отправка отменена, сообщение удалено' };
  }
  if (result.revoked) {
    return { kind: 'success', text: 'Сообщение удалено у клиента и в истории' };
  }
  if (result.not_found_at_client) {
    return { kind: 'success', text: 'Сообщение удалено из истории, у клиента его уже нет' };
  }
  if (scope === 'client' && reason) {
    return {
      kind: 'error',
      text: `Сообщение удалено из истории, у клиента удалить не удалось: ${reason}`,
    };
  }
  return { kind: 'success', text: 'Сообщение удалено из истории' };
}
